import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/feedback/toast";
import type { AppUpdateConfigShape } from "@/lib/app-update/snapshot-to-config";
import { formatAdminDateTime } from "@/lib/format/admin-time";
import { adminFetch } from "@/api";

interface EditEventDetail {
  platform: string;
  config: AppUpdateConfigShape;
}

export interface AppUpdateConfigRow {
  platform: string;
  minVersion: string;
  latestVersion: string;
  blockedVersions: string[];
  forceMessage: string;
  softMessage: string;
  storeUrl: string;
  logoutOnForce: boolean;
  updatedAt: Date | string;
  updatedBy: string | null;
}

export interface AppUpdateFormProps {
  initial: AppUpdateConfigRow;
}

export function AppUpdateForm({ initial }: AppUpdateFormProps) {
  const [minVersion, setMinVersion] = useState(initial.minVersion);
  const [latestVersion, setLatestVersion] = useState(initial.latestVersion);
  const [blocked, setBlocked] = useState(initial.blockedVersions.join(", "));
  const [forceMessage, setForceMessage] = useState(initial.forceMessage);
  const [softMessage, setSoftMessage] = useState(initial.softMessage);
  const [storeUrl, setStoreUrl] = useState(initial.storeUrl);
  const [logoutOnForce, setLogoutOnForce] = useState(initial.logoutOnForce);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onEdit = (e: Event) => {
      const detail = (e as CustomEvent<EditEventDetail>).detail;
      if (!detail || detail.platform !== initial.platform) return;
      const { config } = detail;
      setMinVersion(config.minVersion);
      setLatestVersion(config.latestVersion);
      setBlocked(config.blockedVersions.join(", "));
      setForceMessage(config.forceMessage);
      setSoftMessage(config.softMessage);
      setStoreUrl(config.storeUrl);
      setLogoutOnForce(config.logoutOnForce);
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("fincurry:app-update:edit", onEdit);
    return () => window.removeEventListener("fincurry:app-update:edit", onEdit);
  }, [initial.platform]);

  const save = async () => {
    setSaving(true);
    try {
      const blockedVersions = blocked
        .split(",")
        .map(v => v.trim())
        .filter(v => v.length > 0);

      const res = await adminFetch("/api/app-version", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          platform: initial.platform,
          minVersion,
          latestVersion,
          blockedVersions,
          forceMessage,
          softMessage,
          storeUrl,
          logoutOnForce,
          reason: reason.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        toast.error(data.error ?? "Save failed");
        return;
      }
      toast.success(`${initial.platform} config saved`);
      setReason("");
      window.location.reload();
    } finally {
      setSaving(false);
    }
  };

  const label = (t: string) => (
    <label className="block text-[10.5px] uppercase tracking-wide text-fc-text-dim">
      {t}
    </label>
  );

  const inputCls =
    "w-full rounded-md border border-fc-border bg-fc-bg px-3 py-2 text-[13px] text-fc-text focus:border-fc-brand focus:outline-none";

  return (
    <section
      ref={sectionRef}
      className="flex flex-col gap-4 rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5"
    >
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[15px] font-semibold text-fc-text capitalize">
            {initial.platform}
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Last updated{" "}
            <span className="font-mono">
              {formatAdminDateTime(initial.updatedAt)}
            </span>
            {initial.updatedBy ? ` by ${initial.updatedBy}` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          {label("Min version")}
          <input
            className={inputCls}
            value={minVersion}
            onChange={e => setMinVersion(e.target.value)}
            placeholder="1.0.0"
          />
        </div>
        <div>
          {label("Latest version")}
          <input
            className={inputCls}
            value={latestVersion}
            onChange={e => setLatestVersion(e.target.value)}
            placeholder="1.0.0"
          />
        </div>
      </div>

      <div>
        {label("Blocked versions (comma-separated)")}
        <input
          className={inputCls}
          value={blocked}
          onChange={e => setBlocked(e.target.value)}
          placeholder="1.4.1, 1.4.2"
        />
      </div>

      <div>
        {label("Force message")}
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={forceMessage}
          onChange={e => setForceMessage(e.target.value)}
          placeholder="Shown when current < minVersion or in blocked list"
        />
      </div>

      <div>
        {label("Soft message")}
        <textarea
          className={`${inputCls} min-h-[60px]`}
          value={softMessage}
          onChange={e => setSoftMessage(e.target.value)}
          placeholder="Shown when current < latestVersion (not blocked)"
        />
      </div>

      <div>
        {label("Store URL")}
        <input
          className={inputCls}
          value={storeUrl}
          onChange={e => setStoreUrl(e.target.value)}
          placeholder={
            initial.platform === "android"
              ? "https://play.google.com/…"
              : "https://apps.apple.com/…"
          }
        />
      </div>

      <label className="flex items-center gap-2 text-[13px] text-fc-text">
        <input
          type="checkbox"
          checked={logoutOnForce}
          onChange={e => setLogoutOnForce(e.target.checked)}
          className="accent-fc-brand"
        />
        Log user out on force update
      </label>

      <div>
        {label("Reason for this change (audit)")}
        <input
          className={inputCls}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Blocking 1.4.2 due to OTP bug"
        />
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-b from-fc-brand to-emerald-600 px-4 py-2 text-[13px] font-semibold text-emerald-950 hover:brightness-105 disabled:opacity-60"
      >
        {saving ? "Saving…" : `Save ${initial.platform}`}
      </button>
    </section>
  );
}
